import ArrowBackIosNew from '@mui/icons-material/ArrowBackIosNew';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { VetaCenterConnection } from '~hooks/useVetaCenter/models';
import useVetaCenter from '~hooks/useVetaCenter/useVetaCenter';
import { VETA_CENTER_POLL_WINDOW_IN_MS } from '~hooks/useVetaCenter/useVetaCenterPollWindow';
import { VETA_CENTER_DATE_TIME_FORMAT } from '~hooks/useVetaCenter/vetaCenterConstants';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import useVetaCenterConnectionQuery, {
  getVetaCenterConnectionQueryKey,
} from '~queries/useVetaCenterConnectionQuery';
import useVetaCenterProjectLinksQuery, {
  getVetaCenterProjectLinksQueryKey,
} from '~queries/useVetaCenterProjectLinksQuery';
import { getVetaCenterSourceOperatorsQueryKey } from '~queries/useVetaCenterSourceOperatorsQuery';
import { getVetaCenterTestTicketsQueryKey } from '~queries/useVetaCenterTestTicketsInfiniteQuery';
import { getVetaCenterReadinessItemsQueryKey } from '~queries/vetaCenterReadinessItemsQueryKey';
import { ROUTES as routes } from '~router/routePaths';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { useAwaitableFeatureFlag } from '~utils/hooks/useFeatureFlag';
import useSingleFlight from '~utils/hooks/useSingleFlight';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import VetaCenterConnectForm, { environmentLabelKeys } from './VetaCenterConnectForm';
import VetaCenterOwnFleetForm from './VetaCenterOwnFleetForm';
import VetaCenterProjectLinks from './VetaCenterProjectLinks';
import VetaCenterReadiness from './VetaCenterReadiness';
import VetaCenterTestTickets from './VetaCenterTestTickets';

// Stops polling a discovery that never finishes; Refresh starts it again.
const DISCOVERY_GIVE_UP_AFTER_IN_MS = VETA_CENTER_POLL_WINDOW_IN_MS;

const VetaCenterManagement = observer(() => {
  const { userStore } = useStores();
  const featureFlag = useAwaitableFeatureFlag({
    featureFlagKey: FeatureFlags.vetaCenterIntegration,
  });
  const { canDeleteVetaCenterIntegration, canManageVetaCenterIntegration } =
    userStore.getPermissions();

  if (!featureFlag.isReady) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!featureFlag.isFeatureFlagEnabled || !canManageVetaCenterIntegration) {
    return <Navigate to={routes.base} replace />;
  }

  return <VetaCenterManagementPage canDelete={canDeleteVetaCenterIntegration} />;
});

interface VetaCenterManagementPageProps {
  canDelete: boolean;
}

const VetaCenterManagementPage = observer(
  ({ canDelete }: VetaCenterManagementPageProps) => {
    const { toasterStore, userStore } = useStores();
    const queryClient = useQueryClient();
    const { deleteConnection, refreshContracts } = useVetaCenter();

    const companyId = getEffectiveUserCompanyId(userStore);

    const [discoveryBaselineInMs, setDiscoveryBaselineInMs] = useState<
      number | null | undefined
    >(undefined);
    const [didDiscoveryTimeOut, setDidDiscoveryTimeOut] = useState(false);
    const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] = useState(false);
    const { isInFlight: isMutating, runSingleFlight } = useSingleFlight();

    const {
      isDiscovering,
      isError,
      isLoading,
      refetch: refetchConnection,
      vetaCenterConnection,
    } = useVetaCenterConnectionQuery({ discoveryBaselineInMs });
    const {
      isError: hasProjectLinksError,
      isLoading: isLoadingProjectLinks,
      projectLinks,
    } = useVetaCenterProjectLinksQuery({
      companyId,
      isEnabled: vetaCenterConnection !== null,
    });

    const connectionHasNeverFinishedDiscovery =
      vetaCenterConnection !== null &&
      vetaCenterConnection.contractsDiscoveredAt === null;
    useEffect(() => {
      if (
        connectionHasNeverFinishedDiscovery &&
        discoveryBaselineInMs === undefined &&
        !didDiscoveryTimeOut
      ) {
        setDiscoveryBaselineInMs(null);
      }
    }, [connectionHasNeverFinishedDiscovery, didDiscoveryTimeOut, discoveryBaselineInMs]);

    useEffect(() => {
      if (!isDiscovering) return undefined;

      const giveUpTimeout = setTimeout(() => {
        setDiscoveryBaselineInMs(undefined);
        setDidDiscoveryTimeOut(true);
      }, DISCOVERY_GIVE_UP_AFTER_IN_MS);

      return () => clearTimeout(giveUpTimeout);
    }, [isDiscovering]);

    const invalidateReadinessItems = () =>
      queryClient.invalidateQueries({
        queryKey: getVetaCenterReadinessItemsQueryKey(companyId),
      });

    const saveConnectionToCache = async (saved: VetaCenterConnection) => {
      const queryKey = getVetaCenterConnectionQueryKey(companyId);

      // An in-flight poll would otherwise land after this and overwrite the saved connection.
      await queryClient.cancelQueries({ queryKey });
      queryClient.setQueryData(queryKey, saved);
    };

    const handleConnected = async (created: VetaCenterConnection) => {
      await saveConnectionToCache(created);
      invalidateReadinessItems();
      setDidDiscoveryTimeOut(false);
      setDiscoveryBaselineInMs(created.contractsDiscoveredAt?.valueOf() ?? null);
      toasterStore.push(
        alert(
          t('administration.integration.veta_center.toast.connected'),
          AlertTypes.success,
        ),
      );
    };

    const handleConnectionUpdated = async (updated: VetaCenterConnection) => {
      await saveConnectionToCache(updated);
      invalidateReadinessItems();
      toasterStore.push(
        alert(
          t('administration.integration.veta_center.toast.saved'),
          AlertTypes.success,
        ),
      );
    };

    const handleRefreshClick = () =>
      runSingleFlight(async () => {
        if (!vetaCenterConnection) return;

        // The cached timestamp may predate a refresh another admin already ran.
        const { data: latestConnection } = await refetchConnection();
        const baselineConnection = latestConnection ?? vetaCenterConnection;

        if (await refreshContracts(companyId)) {
          setDidDiscoveryTimeOut(false);
          setDiscoveryBaselineInMs(
            baselineConnection.contractsDiscoveredAt?.valueOf() ?? null,
          );
        }
      });

    const handleDisconnectConfirm = () =>
      runSingleFlight(async () => {
        if (!(await deleteConnection(companyId))) return;

        queryClient.setQueryData(getVetaCenterConnectionQueryKey(companyId), null);
        queryClient.removeQueries({
          queryKey: getVetaCenterProjectLinksQueryKey(companyId),
        });
        queryClient.removeQueries({
          queryKey: getVetaCenterReadinessItemsQueryKey(companyId),
        });
        queryClient.removeQueries({
          queryKey: getVetaCenterTestTicketsQueryKey(companyId),
        });
        queryClient.removeQueries({
          queryKey: getVetaCenterSourceOperatorsQueryKey(companyId),
        });
        setDidDiscoveryTimeOut(false);
        setDiscoveryBaselineInMs(undefined);
        setIsDisconnectDialogOpen(false);
        toasterStore.push(
          alert(
            t('administration.integration.veta_center.toast.disconnected'),
            AlertTypes.success,
          ),
        );
      });

    const handleDisconnectDialogClose = () => {
      if (isMutating) return;

      setIsDisconnectDialogOpen(false);
    };

    const getContractsStatusText = (connected: VetaCenterConnection) => {
      if (isDiscovering) {
        return t('administration.integration.veta_center.contracts.discovering');
      }

      const checkedAtText = connected.contractsDiscoveredAt
        ? ` ${t('administration.integration.veta_center.contracts.checked_at', {
            time: dayjs
              .tz(connected.contractsDiscoveredAt)
              .format(VETA_CENTER_DATE_TIME_FORMAT),
            interpolation: { escapeValue: false },
          })}`
        : '';

      if (connected.contracts.length === 0) {
        return `${t('administration.integration.veta_center.contracts.none')}${checkedAtText}`;
      }

      return `${t('administration.integration.veta_center.contracts.found', {
        count: connected.contracts.length,
      })}${checkedAtText}`;
    };

    const renderContracts = (connected: VetaCenterConnection) => {
      return (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
            <Typography variant="h6">
              {t('administration.integration.veta_center.contracts.title')}
            </Typography>
            <SmallLoadingButton
              color="brandV2Black"
              loading={isDiscovering || isMutating}
              onClick={handleRefreshClick}
              sx={{ borderColor: 'divider', color: 'text.primary' }}
              variant="outlined"
            >
              {t('actions.refresh')}
            </SmallLoadingButton>
          </Box>

          <Typography sx={{ color: 'text.secondary', my: 1 }}>
            {getContractsStatusText(connected)}
          </Typography>

          {didDiscoveryTimeOut ? (
            <Alert severity="info" sx={{ mb: 1 }}>
              {t('administration.integration.veta_center.contracts.still_discovering')}
            </Alert>
          ) : null}

          {connected.contracts.map((contract) => (
            <Box
              key={contract.contractGuid}
              sx={{ borderBottom: 1, borderColor: 'divider', py: 1 }}
            >
              <Typography sx={{ fontWeight: 600 }}>
                {contract.contractId ?? contract.contractGuid}
              </Typography>
              <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                {contract.agencyName}
              </Typography>
            </Box>
          ))}
        </Box>
      );
    };

    const renderBody = () => {
      const connectionLoadError = (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('error_messages.veta_center.failed_to_fetch_connection')}
        </Alert>
      );

      if (isError && !vetaCenterConnection) {
        return connectionLoadError;
      }

      if (isLoading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        );
      }

      if (!vetaCenterConnection) {
        return (
          <>
            <Typography sx={{ color: 'text.secondary', mb: 3 }}>
              {t('administration.integration.veta_center.setup_description')}
            </Typography>
            <VetaCenterConnectForm companyId={companyId} onConnected={handleConnected} />
          </>
        );
      }

      return (
        <>
          {isError ? connectionLoadError : null}

          <Box sx={{ alignItems: 'center', display: 'flex', gap: 2 }}>
            <Typography>
              {t('administration.integration.veta_center.signed_in_as', {
                environment: t(environmentLabelKeys[vetaCenterConnection.environment]),
                interpolation: { escapeValue: false },
                user: vetaCenterConnection.serviceUser,
              })}
            </Typography>
            {canDelete ? (
              <SmallButton
                color="brandV2Black"
                onClick={() => setIsDisconnectDialogOpen(true)}
                sx={{ borderColor: 'divider', color: 'text.primary' }}
                variant="outlined"
              >
                {t('administration.integration.veta_center.cta.disconnect')}
              </SmallButton>
            ) : null}
          </Box>

          <VetaCenterOwnFleetForm
            companyId={companyId}
            key={`${companyId}:${vetaCenterConnection.externalOwnFleetHaulerCompanyGuid ?? ''}`}
            onSaved={handleConnectionUpdated}
            vetaCenterConnection={vetaCenterConnection}
          />

          {renderContracts(vetaCenterConnection)}

          <VetaCenterProjectLinks
            companyId={companyId}
            hasProjectLinksError={hasProjectLinksError}
            isLoadingProjectLinks={isLoadingProjectLinks}
            key={companyId}
            projectLinks={projectLinks}
            vetaCenterConnection={vetaCenterConnection}
          />

          {isLoadingProjectLinks || hasProjectLinksError ? null : (
            <>
              <VetaCenterReadiness
                companyId={companyId}
                hasProjectLinks={projectLinks.length > 0}
              />

              <VetaCenterTestTickets
                companyId={companyId}
                projectLinks={projectLinks}
                vetaCenterConnection={vetaCenterConnection}
              />
            </>
          )}
        </>
      );
    };

    return (
      <Box sx={{ maxWidth: '1000px' }}>
        <Button
          color="secondary"
          component={Link}
          startIcon={<ArrowBackIosNew fontSize="small" />}
          to={`/${routes.settings.base}/${routes.settings.integrations.base}`}
          variant="text"
        >
          {t('administration.integration.title')}
        </Button>
        <SettingsPageHeader
          title={t('administration.integration.veta_center.name')}
          description={t('administration.integration.veta_center.description')}
        />

        {renderBody()}

        <ConfirmationDialog
          confirmButtonProps={{
            children: t('administration.integration.veta_center.cta.disconnect'),
          }}
          isBusy={isMutating}
          isOpen={isDisconnectDialogOpen}
          onClose={handleDisconnectDialogClose}
          onConfirm={handleDisconnectConfirm}
          title={t(
            'administration.integration.veta_center.disconnect_confirmation_title',
          )}
        >
          <Typography>
            {t('administration.integration.veta_center.disconnect_confirmation_body')}
          </Typography>
        </ConfirmationDialog>
      </Box>
    );
  },
);

export default VetaCenterManagement;
